import React, { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import toast from "../utils/toast";

const OrderSuccessPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const hasShownToast = useRef(false);

  useEffect(() => {
    document.title = "Order Success | dawaacare";
    if (!hasShownToast.current) {
      toast.success("Order Placed Successfully!");
      hasShownToast.current = true;
    }
  }, [])

  const state = location.state || {};
  const { orderId, items = [], customerDetails = {}, subtotal = 0, shipping = 0, total = 0 } = state;

  const orderRef = orderId ? orderId.slice(-6).toUpperCase() : null;

  const fullAddress = [
    customerDetails.street,
    customerDetails.address2,
    customerDetails.city,
    customerDetails.region,
    customerDetails.postalCode,
    customerDetails.country || "Pakistan",
  ]
    .filter(Boolean)
    .join(", ");

  const mapSrc = `https://maps.google.com/maps?q=${encodeURIComponent(customerDetails.city)}&output=embed`;

  const paymentLabel =
    customerDetails.paymentMethod === "bank" ? "Bank Deposit" : "Cash on Delivery";

  return (
    <>
      <Header />

      <div className="order-success-wrapper">

        {/* ── LEFT COLUMN ── */}
        <div className="order-success-left">

          {/* Hero banner */}
          <div className="order-success-banner">
            <div className="order-success-icon">
              <i className="fa-solid fa-circle-check"></i>
            </div>
            <div className="order-success-banner-text">
              <h1>Order Placed Successfully</h1>
              <p>
                Thank you, <strong>{customerDetails.firstName} {customerDetails.lastName}</strong>!
                A confirmation email has been sent to{" "}
                <strong>{customerDetails.email}</strong>.
              </p>
              {orderRef && (
                <span className="order-ref-badge">Order Ref: #{orderRef}</span>
              )}
            </div>
          </div>

          {/* Items Ordered */}
          <h2 className="order-success-section-title">Items Ordered</h2>
          <div className="order-success-items">
            {items.map((item) => (
              <div key={item.id} className="order-success-item">
                <img src={item.picture} alt={item.name} />
                <div className="order-success-item-info">
                  <p className="item-name">{item.name}</p>
                  <p className="item-qty">Qty: {item.quantity}</p>
                </div>
                <p className="item-price">Rs. {(item.price * item.quantity).toLocaleString()}</p>
              </div>
            ))}
          </div>

          {/* Customer Information */}
          <h2 className="order-success-section-title">Customer Information</h2>
          <div className="order-success-info-grid">
            <div className="order-success-info-row">
              <span className="info-label">Full Name</span>
              <span className="info-value">{customerDetails.firstName} {customerDetails.lastName}</span>
            </div>
            <div className="order-success-info-row">
              <span className="info-label">Email</span>
              <span className="info-value">{customerDetails.email}</span>
            </div>
            <div className="order-success-info-row">
              <span className="info-label">Phone</span>
              <span className="info-value">{customerDetails.phone}</span>
            </div>
          </div>

          {/* Delivery Address */}
          <h2 className="order-success-section-title">Delivery Address</h2>
          <div className="order-success-info-grid">
            <div className="order-success-info-row">
              <span className="info-label">Street</span>
              <span className="info-value">
                {customerDetails.street}
                {customerDetails.address2 ? `, ${customerDetails.address2}` : ""}
              </span>
            </div>
            <div className="order-success-info-row">
              <span className="info-label">City</span>
              <span className="info-value">{customerDetails.city}</span>
            </div>
            <div className="order-success-info-row">
              <span className="info-label">Province</span>
              <span className="info-value">{customerDetails.region}</span>
            </div>
            <div className="order-success-info-row">
              <span className="info-label">Postal Code</span>
              <span className="info-value">{customerDetails.postalCode}</span>
            </div>
            <div className="order-success-info-row">
              <span className="info-label">Country</span>
              <span className="info-value">{customerDetails.country || "Pakistan"}</span>
            </div>
          </div>

          {/* Payment & Order Summary */}
          <h2 className="order-success-section-title">Order Summary</h2>
          <div className="order-success-info-grid">
            <div className="order-success-info-row">
              <span className="info-label">Payment Method</span>
              <span className="info-value">{paymentLabel}</span>
            </div>
            {customerDetails.orderNotes && (
              <div className="order-success-info-row">
                <span className="info-label">Order Notes</span>
                <span className="info-value">{customerDetails.orderNotes}</span>
              </div>
            )}
            <div className="order-success-info-row">
              <span className="info-label">Subtotal</span>
              <span className="info-value">Rs. {subtotal.toLocaleString()}</span>
            </div>
            <div className="order-success-info-row">
              <span className="info-label">Shipping</span>
              <span className="info-value">Rs. {shipping.toLocaleString()}</span>
            </div>
            <div className="order-success-info-row order-total-row">
              <span className="info-label">Total</span>
              <span className="info-value">Rs. {total.toLocaleString()}</span>
            </div>
          </div>

          <button
            className="continue-shopping-btn"
            onClick={() => navigate("/")}
          >
            Continue Shopping
          </button>
        </div>

        {/* ── RIGHT COLUMN ── */}
        <div className="order-success-right">
          <h2 className="order-success-section-title">Delivery Location</h2>
          <div className="order-map-container">
            <iframe
              title="Delivery Location"
              src={mapSrc}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            ></iframe>
          </div>
          <div className="order-map-address">
            <i className="fa-solid fa-location-dot"></i>
            <p>{fullAddress}</p>
          </div>

          <div className="order-what-next">
            <h3>What happens next?</h3>
            <ul>
              <li>
                <i className="fa-solid fa-envelope"></i>
                You will receive a confirmation email with your order details.
              </li>
              <li>
                <i className="fa-solid fa-box"></i>
                Our team will process and pack your order.
              </li>
              <li>
                <i className="fa-solid fa-truck"></i>
                Your order will be dispatched to the address above.
              </li>
              <li>
                <i className="fa-solid fa-phone"></i>
                Our delivery partner will contact you before delivery.
              </li>
            </ul>
          </div>
        </div>

      </div>

      <Footer />
    </>
  );
};

export default OrderSuccessPage;
